import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Tag, User } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
    } catch {
        return "";
    }
}

// Very lightweight markdown → JSX: paragraphs, **bold**, headings, lists
function renderMarkdown(md) {
    if (!md) return null;
    const lines = md.split("\n");
    const blocks = [];
    let buf = [];
    const flush = () => {
        if (!buf.length) return;
        const text = buf.join(" ").trim();
        if (text) blocks.push({ type: "p", text });
        buf = [];
    };
    let listBuf = null;
    for (const ln of lines) {
        const line = ln.replace(/\s+$/, "");
        if (line.startsWith("# ")) { flush(); listBuf = null; blocks.push({ type: "h1", text: line.slice(2) }); }
        else if (line.startsWith("## ")) { flush(); listBuf = null; blocks.push({ type: "h2", text: line.slice(3) }); }
        else if (line.startsWith("### ")) { flush(); listBuf = null; blocks.push({ type: "h3", text: line.slice(4) }); }
        else if (line.startsWith("- ") || line.startsWith("* ")) {
            flush();
            if (!listBuf) { listBuf = []; blocks.push({ type: "ul", items: listBuf }); }
            listBuf.push(line.replace(/^[-*]\s+/, ""));
        } else if (line.trim() === "") {
            flush(); listBuf = null;
        } else {
            buf.push(line);
        }
    }
    flush();

    const inline = (s) => {
        const parts = s.split(/(\*\*[^*]+\*\*)/g);
        return parts.map((p, i) =>
            p.startsWith("**") && p.endsWith("**")
                ? <strong key={i} className="font-semibold text-brand-ink">{p.slice(2, -2)}</strong>
                : <span key={i}>{p}</span>
        );
    };

    return blocks.map((b, i) => {
        if (b.type === "h1") return <h2 key={i} className="mt-12 font-display text-3xl text-brand-ink sm:text-4xl">{inline(b.text)}</h2>;
        if (b.type === "h2") return <h3 key={i} className="mt-10 font-display text-2xl text-brand-ink sm:text-3xl">{inline(b.text)}</h3>;
        if (b.type === "h3") return <h4 key={i} className="mt-8 font-display text-xl text-brand-ink">{inline(b.text)}</h4>;
        if (b.type === "ul") return (
            <ul key={i} className="my-5 list-disc space-y-2 pl-6 text-base leading-relaxed text-brand-ink/80">
                {b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}
            </ul>
        );
        return <p key={i} className="mt-5 text-base leading-relaxed text-brand-ink/80 sm:text-lg">{inline(b.text)}</p>;
    });
}

export default function ActualitePADetail() {
    const { slug } = useParams();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        setLoading(true);
        api.get(`/actualites/${slug}`)
            .then((r) => {
                setArticle(r.data);
                document.title = `${r.data.title} · Cursus Physio`;
                const meta = document.querySelector('meta[name="description"]') || (() => {
                    const m = document.createElement("meta");
                    m.name = "description";
                    document.head.appendChild(m);
                    return m;
                })();
                meta.content = r.data.excerpt || "Article Cursus Physio";
            })
            .catch((e) => setErr(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) {
        return <main className="bg-white px-6 py-32 text-center text-sm text-brand-smoke">Chargement…</main>;
    }
    if (err || !article) {
        return (
            <main className="bg-white px-6 py-32 text-center" data-testid="actualite-not-found">
                <p className="text-base text-brand-ink">Article introuvable.</p>
                <Link to="/actualite-pa" className="mt-4 inline-block text-sm font-semibold text-brand-orange hover:underline">
                    ← Retour à L'actualité PA
                </Link>
            </main>
        );
    }

    return (
        <main className="bg-white" data-testid="page-actualite-detail">
            {article.cover_url && (
                <div className="relative h-[40vh] w-full overflow-hidden bg-brand-ink">
                    <img src={article.cover_url} alt={article.title} className="h-full w-full object-cover opacity-90" />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40" />
                </div>
            )}
            <article className="mx-auto max-w-3xl px-6 py-16 sm:py-20">
                <Link to="/actualite-pa" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand-orange hover:underline">
                    <ArrowLeft className="h-3.5 w-3.5" /> L'actualité PA
                </Link>
                <motion.h1
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mt-6 font-display text-4xl leading-[1.05] text-brand-ink sm:text-5xl lg:text-6xl"
                >
                    {article.title}
                </motion.h1>
                <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-brand-smoke">
                    {article.published_at && (
                        <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {formatDate(article.published_at)}</span>
                    )}
                    {article.author && (
                        <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {article.author}</span>
                    )}
                    {article.tags?.length > 0 && (
                        <span className="inline-flex flex-wrap items-center gap-1.5">
                            {article.tags.map((t) => (
                                <span key={t} className="inline-flex items-center gap-1 rounded-full border border-brand-orange/30 bg-brand-orange/5 px-2.5 py-0.5 text-[10px] font-medium text-brand-ink">
                                    <Tag className="h-2.5 w-2.5 text-brand-orange" /> {t}
                                </span>
                            ))}
                        </span>
                    )}
                </div>
                {article.excerpt && (
                    <p className="mt-8 border-l-2 border-brand-orange pl-5 text-lg italic leading-relaxed text-brand-ink/75 sm:text-xl">
                        {article.excerpt}
                    </p>
                )}
                <div className="mt-10 text-brand-ink/85">
                    {renderMarkdown(article.content_md)}
                </div>
            </article>
        </main>
    );
}
